import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  compareFacilityInstructorDelivery,
  type InstructorInsightsResponse,
} from "@/libs/analyticsApi";

const completedFourWeekWindow = () => {
  const endBoundary = new Date();
  endBoundary.setUTCHours(0, 0, 0, 0);
  endBoundary.setUTCDate(
    endBoundary.getUTCDate() - ((endBoundary.getUTCDay() + 6) % 7),
  );
  const start = new Date(endBoundary);
  start.setUTCDate(start.getUTCDate() - 28);
  const lastIncluded = new Date(endBoundary);
  lastIncluded.setUTCDate(lastIncluded.getUTCDate() - 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: lastIncluded.toISOString().slice(0, 10),
  };
};
const asInstant = (date: string) => `${date}T00:00:00Z`;
const afterInclusiveDate = (date: string) => {
  const end = new Date(asInstant(date));
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
};
const formatUtcDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(new Date(value));
const lastIncludedDate = (exclusiveEnd: string) => {
  const date = new Date(exclusiveEnd);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString();
};
const formatMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};
const dateProps = {
  fullWidth: true,
  size: "small" as const,
  slotProps: { inputLabel: { shrink: true } },
  type: "date",
};

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }} variant="h6">
        {value}
      </Typography>
      <Typography color="text.secondary" variant="caption">{label}</Typography>
    </Box>
  );
}

function InstructorCard({ instructor }: { instructor: InstructorInsightsResponse["instructors"][number] }) {
  return (
    <Paper component="article" sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">{instructor.instructor_name}</Typography>
            <Typography color="text.secondary" variant="body2">
              {instructor.distinct_programs} Program{instructor.distinct_programs === 1 ? "" : "s"} with certified delivery
            </Typography>
          </Box>
          <Chip icon={<AccessTimeOutlinedIcon />} label={formatMinutes(instructor.attributed_minutes)} variant="outlined" />
        </Stack>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" } }}>
          <Metric label="Session contributions" value={instructor.contributed_sessions} />
          <Metric label="Completed" value={instructor.completed_sessions} />
          <Metric label="Partial" value={instructor.partial_sessions} />
          <Metric label="Attributed time" value={formatMinutes(instructor.attributed_minutes)} />
        </Box>
        <Divider />
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <Chip label={`${instructor.head_sessions} head`} size="small" />
          <Chip label={`${instructor.substitute_sessions} substitute`} size="small" />
          <Chip label={`${instructor.assistant_sessions} assistant`} size="small" />
        </Stack>
      </Stack>
    </Paper>
  );
}

export function InstructorInsightsWorkspace() {
  const { user } = useAuth();
  const orgView = useMyOrg();
  const { facility, facilityId, isLoading: facilityLoading } = useResolvedFacility();
  const initialWindow = useRef(completedFourWeekWindow()).current;
  const [start, setStart] = useState(initialWindow.start);
  const [end, setEnd] = useState(initialWindow.end);
  const [result, setResult] = useState<InstructorInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"unavailable" | "forbidden" | "invalid" | "retry" | null>(null);
  const generation = useRef(0);
  const activeFacilityId = useRef(facilityId);
  activeFacilityId.current = facilityId;
  const organizationOwner = Boolean(user?.uid && orgView.org?.owner_user_id === user.uid);

  useEffect(() => {
    generation.current += 1;
    setResult(null);
    setError(null);
    setLoading(false);
  }, [facilityId]);

  const editWindow = (setter: (value: string) => void, value: string) => {
    generation.current += 1;
    setter(value);
    setResult(null);
    setError(null);
    setLoading(false);
  };

  const load = async () => {
    if (!facilityId || loading) return;
    const requestFacility = facilityId;
    const requestGeneration = ++generation.current;
    const current = () =>
      generation.current === requestGeneration && activeFacilityId.current === requestFacility;
    setLoading(true);
    setError(null);
    try {
      const response = await compareFacilityInstructorDelivery(requestFacility, {
        start: asInstant(start),
        end: afterInclusiveDate(end),
      });
      if (!current()) return;
      if (response.facility_id !== requestFacility) {
        setError("retry");
        return;
      }
      setResult(response);
    } catch (cause) {
      if (!current()) return;
      setResult(null);
      if (isApiError(cause) && cause.status === 503) setError("unavailable");
      else if (isApiError(cause) && cause.status === 403) setError("forbidden");
      else if (isApiError(cause) && cause.status === 422) setError("invalid");
      else setError("retry");
    } finally {
      if (current()) setLoading(false);
    }
  };

  if (facilityLoading || !facilityId) {
    return (
      <Stack aria-label="Loading instructor insights" role="status" spacing={1} sx={{ alignItems: "center", justifyContent: "center", minHeight: "40vh" }}>
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">Loading…</Typography>
      </Stack>
    );
  }

  const messages = {
    unavailable: "Instructor insights aren’t enabled for this organization. Programs, schedules, attendance, teaching history, receipts, and exports remain available.",
    forbidden: "You don’t have permission to view internal instructor insights.",
    invalid: "Choose 1 to 13 complete UTC weeks, Monday through Sunday.",
    retry: "We couldn’t build this instructor report. Review the dates and try again.",
  };

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}>
        <Box>
          <Typography component="h1" sx={{ fontWeight: 800 }} variant="h5">Instructor delivery</Typography>
          <Typography color="text.secondary" variant="body2">Review club-certified staffing workload at {facility?.name ?? "this facility"}.</Typography>
        </Box>
        <Chip color="primary" icon={<SchoolOutlinedIcon />} label="Insights module" variant="outlined" />
      </Stack>

      <Paper component="form" onSubmit={(event) => { event.preventDefault(); void load(); }} sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
        <Stack spacing={2}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 700 }} variant="h6">Select a completed period</Typography>
            <Typography color="text.secondary" variant="body2">Use complete UTC weeks, Monday through Sunday. Results come from current revisions of club-certified teaching evidence.</Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField {...dateProps} label="Start date" onChange={(event) => editWindow(setStart, event.target.value)} value={start} />
            <TextField {...dateProps} label="Last included day" onChange={(event) => editWindow(setEnd, event.target.value)} value={end} />
          </Stack>
          <Button disabled={loading} sx={{ alignSelf: "flex-start" }} type="submit" variant="contained">{loading ? "Building report…" : "Build instructor report"}</Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert
          action={error === "unavailable" && organizationOwner ? (
            <Button color="inherit" component={RouterLink} size="small" to="/settings/contract">Review modules</Button>
          ) : error === "retry" ? (
            <Button color="inherit" onClick={() => void load()} size="small">Retry</Button>
          ) : undefined}
          severity={error === "unavailable" ? "info" : "error"}
        >{messages[error]}</Alert>
      ) : null}

      {result ? (
        <Stack spacing={2.5}>
          <Box>
            <Typography color="text.secondary" variant="body2">Included period: {formatUtcDate(result.start)}–{formatUtcDate(lastIncludedDate(result.end))}</Typography>
            <Typography color="text.secondary" variant="caption">Generated {formatUtcDate(result.generated_at)} · {result.freshness}</Typography>
          </Box>
          <Typography color="text.secondary" variant="body2">{result.decision_supported}</Typography>
          <Paper component="section" sx={{ p: { xs: 2, sm: 2.5 } }} variant="outlined">
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" } }}>
              <Metric label="Instructors" value={result.totals.instructors} />
              <Metric label="Instructor-session contributions" value={result.totals.contributed_sessions} />
              <Metric label="Attributed teaching time" value={formatMinutes(result.totals.attributed_minutes)} />
            </Box>
          </Paper>
          {result.instructors.length ? result.instructors.map((instructor, index) => (
            <InstructorCard instructor={instructor} key={`${instructor.instructor_name}-${index}`} />
          )) : (
            <Alert severity="info">No club-certified instructor delivery was recorded in this period. Attendance and Program history remain available.</Alert>
          )}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">How to read this report</Typography>
            <Stack component="ul" spacing={0.75} sx={{ color: "text.secondary", mb: 0, mt: 1, pl: 2.5 }}>
              {result.limitations.map((limitation) => <Typography component="li" key={limitation} variant="body2">{limitation}</Typography>)}
            </Stack>
          </Paper>
        </Stack>
      ) : null}
    </Stack>
  );
}
